/**
 * 9-slice del camino de adoquín (`pattern2`) a nivel de TILE (1×1).
 *
 * Extraído de `worldGenerator.ts` para que tanto el generador como los **sectores
 * custom** (`sectors.ts`) puedan dibujar caminos de adoquín idénticos sin duplicar
 * el mapping ni crear un import circular (worldGenerator importa sectors).
 *
 * El "blob" de adoquín es un rectángulo redondeado: sus 9 regiones (centro sólido,
 * 4 bordes rectos y 4 esquinas redondeadas) se muestrean como tiles individuales
 * del atlas (cols 0..5 × rows 0..5). Los bordes rectos usan el tile del MEDIO del
 * lateral (recto, sin ondulación); las esquinas usan los tiles de esquina. Se
 * dibuja en capa 2 sobre el terreno (que se difumina en los bordes).
 * Ver docs/WORLD_BUILDER.md.
 */
/** Las 13 piezas 1×1 del blob 9-slice para un pattern atlas dado. */
export interface Blob9Pieces {
  c: string;
  t: string;
  b: string;
  l: string;
  r: string;
  tl: string;
  tr: string;
  bl: string;
  br: string;
  innerTL: string;
  innerTR: string;
  innerBL: string;
  innerBR: string;
}

/**
 * Arma el mapping de piezas del blob para cualquier pattern con el MISMO layout
 * que pattern2 (rectángulo redondeado 6×6 + mordidas internas a la derecha).
 * Lo usan el camino de adoquín (`pattern2`) y el piso de los muelles
 * (`DOCK_PATTERN_ID` en worldGenerator). Ver docs/WATER_AND_BOATS.md §Muelles.
 */
export function blob9Pieces(patternId: string): Blob9Pieces {
  return {
    c: `${patternId}_2_2_1x1`, // centro sólido (tilea sin costura)
    t: `${patternId}_2_0_1x1`, // borde superior recto
    b: `${patternId}_2_5_1x1`, // borde inferior recto
    l: `${patternId}_0_2_1x1`, // borde izquierdo recto
    r: `${patternId}_5_2_1x1`, // borde derecho recto
    tl: `${patternId}_0_0_1x1`,
    tr: `${patternId}_5_0_1x1`,
    bl: `${patternId}_0_5_1x1`,
    br: `${patternId}_5_5_1x1`,
    // Esquinas internas (cóncavas): mordida hacia la diagonal sin camino.
    innerTL: `${patternId}_9_3_1x1`, // falta NW
    innerTR: `${patternId}_8_3_1x1`, // falta NE
    innerBL: `${patternId}_9_2_1x1`, // falta SW
    innerBR: `${patternId}_8_2_1x1`, // falta SE
  };
}

export const PATH9: Blob9Pieces = blob9Pieces('pattern2');

/**
 * Elige el tile 9-slice del camino. n/s/e/w/diagonales = el vecino TAMBIÉN es camino.
 * Borde recto donde no hay camino orto; esquina externa donde faltan dos ortos;
 * esquina interna donde están los 4 ortos pero falta una diagonal (cruce/T).
 */
export function path9Sprite(
  n: boolean,
  s: boolean,
  e: boolean,
  w: boolean,
  nw: boolean,
  ne: boolean,
  sw: boolean,
  se: boolean,
  pieces: Blob9Pieces = PATH9
): string {
  if (!n && !w) return pieces.tl;
  if (!n && !e) return pieces.tr;
  if (!s && !w) return pieces.bl;
  if (!s && !e) return pieces.br;
  if (!n) return pieces.t;
  if (!s) return pieces.b;
  if (!w) return pieces.l;
  if (!e) return pieces.r;
  // Interior: si falta una diagonal, esquina interna que la redondea.
  if (!nw) return pieces.innerTL;
  if (!ne) return pieces.innerTR;
  if (!sw) return pieces.innerBL;
  if (!se) return pieces.innerBR;
  return pieces.c;
}
